import { Injectable } from '@angular/core';

@Injectable()
export class UtilsService {

  constructor() { }

  /**
   * Generate unique transaction ID
   */
  generateTxnId(): string {
    const STORAGE_KEY = 'lastTxnNumber';
    // default start at 10000 so first becomes TXN10001
    const last = Number(localStorage.getItem(STORAGE_KEY)) || 10000;
    const next = last + 1;
    localStorage.setItem(STORAGE_KEY, String(next));
    return 'TXN' + next;
  }

  /**
   * Format amount to 2 decimal places
   */
  formatAmount(amount: number | string): string {
    const num = (typeof amount === 'string') ? parseFloat(amount) : amount;
    return num.toFixed(2);
  }

  /**
   * Create and submit a form dynamically
   */
  createAndSubmitForm(actionUrl: string, formFields: { [k: string]: any }): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = actionUrl;
    form.style.display = 'none';

    Object.keys(formFields).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = formFields[key] == null ? '' : String(formFields[key]);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Parse Worldline pipe-delimited response message
   * 
   * Format: statusCode|status|statusType|statusMessage|txnId|bankTxnId|merchantId|amount|customerInfo|dateTime|paymentMode|...|sessionId|checksum
   * 
   * Example: "0399|FAILED|Normal+FAILED+Transaction|Transaction+Failed+at+Bank+End+FAILURE|TXN10023|13820|766948764|1.00|{email:test@gmail.com}{mob:1234567890}|24-12-2025+15:43:41|NA||||session123|checksum456"
   * 
   * @param msg Pipe-delimited message string from Worldline
   * @returns Parsed object with transaction details
   */
  parseWorldlineMessage(msg: string): any {
    if (!msg) {
      return null;
    }

    try {
      // Decode URL encoding and replace + with space
      const decodedMsg = decodeURIComponent(msg).replace(/\+/g, ' ');
      
      // Split by pipe delimiter
      const parts = decodedMsg.split('|');
      
      // Parse customer info (email and mobile)
      let customerEmail = '';
      let customerMobile = '';
      
      if (parts[8]) {
        const customerInfo = parts[8];
        const emailMatch = customerInfo.match(/\{email:([^}]+)\}/);
        const mobileMatch = customerInfo.match(/\{mob:([^}]+)\}/);
        
        if (emailMatch) {
          customerEmail = emailMatch[1];
        }
        if (mobileMatch) {
          customerMobile = mobileMatch[1];
        }
      }
      
      return {
        statusCode: parts[0] || '',           // 0399
        status: parts[1] || '',               // FAILED
        statusType: parts[2] || '',           // Normal FAILED Transaction
        statusMessage: parts[3] || '',        // Transaction Failed at Bank End FAILURE
        txnId: parts[4] || '',                // TXN10023
        bankTxnId: parts[5] || '',            // 13820
        merchantId: parts[6] || '',           // 766948764
        amount: parts[7] || '',               // 1.00
        customerEmail: customerEmail,         // Extracted from parts[8]
        customerMobile: customerMobile,       // Extracted from parts[8]
        dateTime: parts[9] || '',             // 24-12-2025 15:43:41
        paymentMode: parts[10] || 'NA',       // Payment mode
        sessionId: parts[14] || '',           // Session ID
        checksum: parts[15] || '',            // Checksum
        rawMessage: msg                       // Store original message
      };
    } catch (error) {
      console.error('Error parsing Worldline message:', error);
      return {
        error: true,
        message: 'Failed to parse Worldline response',
        rawMessage: msg
      };
    }
  }

  /**
   * Validate Worldline response format
   */
  isValidWorldlineResponse(msg: string): boolean {
    if (!msg) return false;
    
    const parts = msg.split('|');
    
    // Valid response should have at least 10 parts
    return parts.length >= 10;
  }

  /**
   * Extract transaction ID from Worldline response
   */
  extractTxnIdFromWorldlineMsg(msg: string): string {
    if (!msg) return '';
    
    try {
      const parts = msg.split('|');
      return parts[4] || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Extract status code from Worldline response
   */
  extractStatusCodeFromWorldlineMsg(msg: string): string {
    if (!msg) return '';
    
    try {
      const parts = msg.split('|');
      return parts[0] || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString.replace(/\+/g, ' '));
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  }

  /**
   * Generate unique request ID
   */
  generateRequestId(): string {
    return 'REQ_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Sanitize string for display
   */
  sanitizeString(str: string): string {
    if (!str) return '';
    
    return str
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Deep clone object
   */
  deepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (e) {
      console.error('Error cloning object:', e);
      return obj;
    }
  }

  /**
   * Check if object is empty
   */
  isEmpty(obj: any): boolean {
    if (obj === null || obj === undefined) return true;
    if (typeof obj === 'string') return obj.trim().length === 0;
    if (Array.isArray(obj)) return obj.length === 0;
    if (typeof obj === 'object') return Object.keys(obj).length === 0;
    return false;
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number | string, currency: string = 'INR'): string {
    const num = (typeof amount === 'string') ? parseFloat(amount) : amount;
    
    if (isNaN(num)) return '0.00';
    
    if (currency === 'INR') {
      return '₹' + num.toFixed(2);
    }
    
    return currency + ' ' + num.toFixed(2);
  }

  /**
   * Retry mechanism for async operations
   */
  async retry<T>(
    fn: () => Promise<T>, 
    maxRetries: number = 3, 
    delay: number = 1000
  ): Promise<T> {
    let lastError: any;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        console.log(`Retry attempt ${i + 1} failed:`, error);
        
        if (i < maxRetries - 1) {
          await this.sleep(delay * (i + 1));
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Sleep utility
   */
  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Log with timestamp
   */
  log(message: string, data?: any): void {
    const timestamp = new Date().toISOString();
    if (data) {
      console.log(`[${timestamp}] ${message}`, data);
    } else {
      console.log(`[${timestamp}] ${message}`);
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/login/services/utils.service.ts